// UserForm.tsx - Updated with Azure AD Guest Invitation functionality, scroll prevention + Help Panel
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FocalPointsManagement.css'; 
import '../companyManagement/CompanyFormOverrides.css';
import { FocalPointsApiService } from './apiService';
import { 
  User, 
  Company, 
  Function, 
  UserFormData, 
  UserType, 
  FocalPointsManagementProps 
} from './types';
import { RoleSelector, COMPANY_ROLES, GATE_ROLES, stringToRoles } from './RoleSelector';

// Add spinner animation styles
const spinnerStyles = `
  @keyframes invitation-spinner {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface UserFormProps extends FocalPointsManagementProps {
  editingUser: User | null;
  selectedUserType: UserType;
  selectedCompany: Company | null;
  functions: Function[];
  isLoadingFunctions: boolean;
  onBack: () => void;
  onSuccess: () => void;
}

// ─── Help Panel Content ─────────────────────────────────────────────
interface HelpItem {
  title: string;
  description: string;
  tip?: string;
  details?: { label: string; text: string }[];
}

const HELP_CONTENT: Record<string, HelpItem> = {
  default: {
    title: 'User Registration',
    description: 'Use this form to add a new user to the gate pass system or update an existing user\'s details. Users can be either company users (associated with a specific organisation) or gate users (facility security personnel).',
    tip: 'All fields marked with * are required. Click on any field to see specific guidance.'
  },
  userType: {
    title: 'User Type',
    description: 'Indicates whether this user belongs to a company or is a gate/security user. This determines which roles and permissions are available.',
    tip: 'The user type is set based on your current context and cannot be changed here.'
  },
  userName: {
    title: 'User Name',
    description: 'Enter the user\'s full legal name as it should appear on gate passes and in official communications.',
    tip: 'Use the name as it appears on the user\'s identification documents.'
  },
  userEmail: {
    title: 'User Email',
    description: 'The user\'s email address is used for login, notifications, and task assignments. If the email domain differs from the company domain, an Azure AD guest invitation can be sent.',
    tip: 'Use the user\'s work email address. External users will be flagged automatically for guest invitation.'
  },
  adInvitation: {
    title: 'Azure AD Guest Invitation',
    description: 'When an external user is detected (email domain differs from the company), you can send an Azure AD B2B guest invitation to grant them secure access to the system.',
    tip: 'The invitation email is sent automatically upon user creation. The user must accept the invitation to gain access.'
  },
  userPhoneNo: {
    title: 'Phone Number',
    description: 'Provide a contact phone number for the user. This may be used for urgent communications regarding gate pass approvals or security matters.',
    tip: 'Include the country code for international numbers (e.g. +974 for Qatar).'
  },
  nationality: {
    title: 'Nationality',
    description: 'Record the user\'s nationality. This information may be required for security clearance and facility access documentation.',
    tip: 'Enter the nationality as it appears on the user\'s passport or official ID.'
  },
  functionId: {
    title: 'Division',
    description: 'Optionally assign the user to a division within the company. This determines which approval workflow the user belongs to.',
    tip: 'Divisions can also be managed separately via the "Manage Divisions" section.'
  },
  role: {
    title: 'Roles',
    description: 'Assign one or more roles to define the user\'s permissions. A user can hold multiple roles simultaneously.',
    tip: 'The base role (Standard User / Gate User) is always active and cannot be removed.',
    details: [
      { label: 'Standard user', text: 'Base role for all company users. Allows submitting gate pass requests and viewing their own request status.' },
      { label: 'Focal point', text: 'Primary liaison between the company and the facility. Receives notifications, can view all company requests, and coordinates with gate personnel. Focal points also gain access to the Divisions tab, allowing them to create, edit, and manage divisions within their own company — including assigning approvers and reviewers to each division.' },
      { label: 'Company reviewer', text: 'First stage of the company approval workflow. Reviews submitted requests and can recommend approval or request changes before the approver sees them.' },
      { label: 'Company approver', text: 'Final authority at the company level. Approves or rejects requests after they have been reviewed. Approved requests proceed to the gate/asset stage.' },
      { label: 'Gate / asset reviewer', text: 'Reviews requests at the facility gate or asset level. Verifies documentation, personnel details, and compliance before gate-level approval.' },
      { label: 'Gate / asset approver', text: 'Final gate-level authority. Grants or denies physical access at assigned gates or assets. This is the last approval before a gate pass is issued.' },
    ]
  },
  isBlacklisted: {
    title: 'Blacklist User',
    description: 'Blacklisting a user will immediately revoke their access to the application. They will not be able to log in or submit any gate pass requests.',
    tip: 'Use this for users whose access needs to be suspended. This can be reversed later by un-checking the option.'
  }
};

export const UserForm: React.FC<UserFormProps> = ({
  editingUser,
  selectedUserType,
  selectedCompany,
  functions,
  isLoadingFunctions,
  getAccessToken,
  onStatusUpdate,
  onBack,
  onSuccess
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    userName: '',
    userEmail: '',
    userPhoneNo: '',
    isBlacklisted: false,
    userType: selectedUserType,
    companyId: null,
    functionId: 0,
    roles: selectedUserType === 'company' ? ['user'] : ['gate_user'],
    nationality: ''
  });
  
  const [isExternalUser, setIsExternalUser] = useState(false);
  const [sendADInvitation, setSendADInvitation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [apiService] = useState(() => new FocalPointsApiService(getAccessToken));
  const [focusedField, setFocusedField] = useState<string>('default');
  const [showHelp, setShowHelp] = useState<boolean>(true);

  // Refs for scroll prevention
  const modalRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  // Prevent scroll propagation to background
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const modalBody = modalBodyRef.current;
    if (!modalBody) return;

    const { scrollTop, scrollHeight, clientHeight } = modalBody;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;

    if (isScrollingDown && scrollTop + clientHeight >= scrollHeight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isScrollingUp && scrollTop <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();
  }, []);

  // Prevent event bubbling from modal content
  const handleModalContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

   const handleClose = useCallback(() => {
    setFormData({
      userName: '',
      userEmail: '',
      userPhoneNo: '',
      isBlacklisted: false,
      userType: selectedUserType,
      companyId: selectedUserType === 'gate' ? null : (selectedCompany?.companyId || null),
      functionId: 0,
      roles: selectedUserType === 'company' ? ['user'] : ['gate_user'],
      nationality: ''
    });
    setIsExternalUser(false);
    setSendADInvitation(false);
    onBack();
  }, [selectedUserType, selectedCompany, onBack]);

  // Handle overlay click to close modal
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const isEmailExternal = useCallback((email: string): boolean => {
    if (!email || !selectedCompany) return false;
    
    const emailDomain = email.split('@')[1]?.toLowerCase();
    
    return emailDomain ? emailDomain !== 'gateqx.com' : false;
  }, [selectedCompany]);

  useEffect(() => {
    if (editingUser) {
      // Parse comma-separated role string into array, ensure default role is present
      const parsedRoles = stringToRoles(editingUser.role);
      const defaultRole = editingUser.userType === 'company' ? 'user' : 'gate_user';
      const rolesWithDefault = parsedRoles.length > 0 ? parsedRoles : [defaultRole];
      // Ensure the always-on default role is present
      if (!rolesWithDefault.includes(defaultRole)) {
        rolesWithDefault.unshift(defaultRole);
      }

      setFormData({
        userName: editingUser.userName,
        userEmail: editingUser.userEmail,
        userPhoneNo: editingUser.userPhoneNo,
        isBlacklisted: editingUser.isBlacklisted,
        userType: editingUser.userType,
        companyId: editingUser.companyId,
        functionId: editingUser.functionId || 0,
        roles: rolesWithDefault,
        nationality: editingUser.nationality
      });
      
      if (editingUser.userEmail && selectedCompany) {
        setIsExternalUser(isEmailExternal(editingUser.userEmail));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        userType: selectedUserType,
        companyId: selectedUserType === 'gate' ? null : (selectedCompany?.companyId || null),
        roles: selectedUserType === 'company' ? ['user'] : ['gate_user']
      }));
      setIsExternalUser(false);
      setSendADInvitation(false);
    }
  }, [editingUser, selectedUserType, selectedCompany, isEmailExternal]);

  useEffect(() => {
    if (formData.userEmail && selectedCompany) {
      const external = isEmailExternal(formData.userEmail);
      setIsExternalUser(external);
      
      if (external && !editingUser) {
        setSendADInvitation(true);
      }
    } else {
      setIsExternalUser(false);
      setSendADInvitation(false);
    }
  }, [formData.userEmail, selectedCompany, editingUser, isEmailExternal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'functionId') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSendingInvitation(false);

    try {
      let result;
      
    if (editingUser) {
      result = await apiService.updateUser(editingUser.userId, formData);
    } else {
      result = await apiService.createUser(formData);
    }

      if (result.success) {
        if (!editingUser && sendADInvitation && isExternalUser) {
          setIsSendingInvitation(true);
          onStatusUpdate({
            type: 'success',
            message: '✅ User created successfully. Sending Azure AD invitation... (Please wait)'
          });
          
          const invitationResult = await apiService.sendADInvitation({
            email: formData.userEmail,
            displayName: formData.userName,
            sendInvitationMessage: true,
            inviteRedirectUrl: 'https://victorious-moss-04770cc03.2.azurestaticapps.net'
          });
          
          setIsSendingInvitation(false);
          
          if (invitationResult.success) {
            onStatusUpdate({
              type: 'success',
              message: `✅ Complete! User created and invitation email sent to ${formData.userEmail}. They will receive an email to accept the invitation.`
            });
          } else {
            onStatusUpdate({
              type: 'error',
              message: `⚠️ User created successfully, but invitation failed: ${invitationResult.message}. You may need to send the invitation manually.`
            });
          }
        } else {
          onStatusUpdate({
            type: 'success',
            message: result.message || 'Operation completed successfully'
          });
        }
        
        onSuccess();
      } else {
        onStatusUpdate({
          type: 'error',
          message: result.message || 'Operation failed'
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setIsSendingInvitation(false);
      onStatusUpdate({
        type: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
      setIsSendingInvitation(false);
    }
  };

  const getFormTitle = () => {
    if (editingUser) {
      return 'Edit User';
    } else {
      return selectedUserType === 'company' ? 'Add Company User' : 'Add Gate User';
    }
  };

  // Get current help content based on focused field
  const currentHelp = HELP_CONTENT[focusedField] || HELP_CONTENT.default;

  return (
    <>
      <style>{spinnerStyles}</style>
      <div 
        className="modal-overlay"
        onClick={handleOverlayClick}
        onWheel={(e) => e.preventDefault()}
      >
      <div 
        ref={modalRef}
        className={`modal-container ${showHelp ? 'company-form-with-help' : ''}`}
        onClick={handleModalContentClick}
        onWheel={handleWheel}
      >
        <div className="modal-header">
          <h2 className="modal-title">{getFormTitle()}</h2>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="modal-content-with-help">
          {/* ─── Left: Form ─────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="modal-form company-form-main">
            <div ref={modalBodyRef} className="modal-body">
              <div className="form-field" onClick={() => setFocusedField('userType')}>
                <label className="field-label">User Type</label>
                <div className="user-type-context">
                  <span className={`user-type-indicator ${selectedUserType}`}>
                    {selectedUserType === 'company' ? 'Company User' : 'Gate User'}
                  </span>
                  {selectedUserType === 'company' && selectedCompany && (
                    <span className="company-context">
                      for {selectedCompany.companyName}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field" onFocus={() => setFocusedField('userName')}>
                <label htmlFor="userName" className="field-label">
                  User Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  placeholder="Enter user's full name"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('userEmail')}>
                <label htmlFor="userEmail" className="field-label">
                  User Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="userEmail"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
                {isExternalUser && selectedUserType === 'company' && (
                  <div className="field-info external-user-notice" style={{ color: 'black' }}>
                    External user detected - email domain differs from company domain
                  </div>
                )}
              </div>

              {!editingUser && isExternalUser && selectedUserType === 'company' && (
                <div className="ad-invitation-section" onClick={() => setFocusedField('adInvitation')}>
                  <div className="ad-invitation-header">
                    
                    <div className="ad-invitation-content" style={{ color: 'black' }}>
                      <h4>Microsoft Entra ID Integration</h4>
                      <p className="ad-invitation-description" style={{ color: 'black' }}>
                      
                        You can send an Azure AD B2B guest invitation to grant them secure access.
                      </p>
                    </div>
                  </div>

                   <div className="form-checkboxes">
                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="sendADInvitation"
                    name="sendADInvitation"
                    checked={sendADInvitation}
                    onChange={(e) => setSendADInvitation(e.target.checked)}
                    className="field-checkbox"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="sendADInvitation" className="checkbox-label">
                    Send Azure AD Guest Invitation
                  </label>
                </div>
              </div>
                  
                  

                  {sendADInvitation && (
                    <div className="ad-invitation-details">
                      <div className="ad-detail-item">
                        <span className="ad-detail-icon">📧</span>
                        <span className="ad-detail-text" style={{ color: 'black' }}>
                          Invitation email will be sent to: <strong>{formData.userEmail}</strong>
                        </span>
                      </div>
                     
                     
                    </div>
                  )}
                </div>
              )}

              <div className="form-field" onFocus={() => setFocusedField('userPhoneNo')}>
                <label htmlFor="userPhoneNo" className="field-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="userPhoneNo"
                  name="userPhoneNo"
                  value={formData.userPhoneNo}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className="field-input"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('nationality')}>
                <label htmlFor="nationality" className="field-label">
                  Nationality
                </label>
                <input
                  type="text"
                  id="nationality"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  placeholder="e.g., American, Indian, British"
                  className="field-input"
                  disabled={isSubmitting}
                />
              </div>

              {selectedUserType === 'company' && !isLoadingFunctions && functions.length > 0 && (
                <div className="form-field" onFocus={() => setFocusedField('functionId')}>
                  <label htmlFor="functionId" className="field-label">
                    Division
                  </label>
                  <select
                    id="functionId"
                    name="functionId"
                    value={formData.functionId}
                    onChange={handleInputChange}
                    className="field-select"
                    disabled={isSubmitting || isLoadingFunctions}
                  >
                    <option value={0}>Select a division (optional)</option>
                    {functions.map((func) => (
                      <option key={func.functionId} value={func.functionId}>
                        {func.functionName}
                      </option>
                    ))}
                  </select>
                  <div className="field-help">
                    Divisions can be managed separately via 'Manage Divisions'
                  </div>
                </div>
              )}

              <div className="form-field" onFocus={() => setFocusedField('role')} onClick={() => setFocusedField('role')}>
                <label className="field-label">
                  Roles
                </label>
                <RoleSelector
                  roles={selectedUserType === 'company' ? COMPANY_ROLES : GATE_ROLES}
                  selectedRoles={formData.roles}
                  onChange={(newRoles) => setFormData(prev => ({ ...prev, roles: newRoles }))}
                  disabled={isSubmitting}
                />
              </div>

              {editingUser && (
                <>
                  <div className="form-checkboxes" onClick={() => setFocusedField('isBlacklisted')}>
                    <div className="checkbox-field">
                      <input
                        type="checkbox"
                        id="isBlacklisted"
                        name="isBlacklisted"
                        checked={formData.isBlacklisted}
                        onChange={handleInputChange}
                        className="field-checkbox"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="isBlacklisted" className="checkbox-label">
                        Blacklist this user
                      </label>
                    </div>
                  </div>
                  
                  <div className="field-help blacklist-help">
                    Blacklisted users cannot access the application
                  </div>
                </>
              )}
            </div>

            {/* Invitation Progress Indicator */}
            {isSendingInvitation && (
              <div style={{
                padding: '16px',
                margin: '16px 0',
                backgroundColor: '#e3f2fd',
                border: '2px solid #2196f3',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #2196f3',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'invitation-spinner 1s linear infinite'
                }}></div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    color: '#1976d2',
                    marginBottom: '4px'
                  }}>
                    📧 Sending Azure AD Invitation...
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#555'
                  }}>
                    This may take a few moments. Please wait...
                  </div>
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  editingUser ? 'Updating...' : (sendADInvitation ? 'Creating & Inviting...' : 'Creating...')
                ) : (
                  editingUser ? 'Update User' : (sendADInvitation && isExternalUser ? 'Create User & Send Invitation' : 'Create User')
                )}
              </button>
            </div>
          </form>

          {/* ─── Help Panel (collapsible with animation) ────────────── */}
          <div className={`company-help-panel ${showHelp ? 'help-panel-open' : 'help-panel-closed'}`}>
            <div className="help-panel-inner">
              <div className={`help-panel-card ${focusedField !== 'default' ? 'help-active' : ''}`}>
                <h4 className="help-card-title">{currentHelp.title}</h4>
                <p className="help-card-description">{currentHelp.description}</p>
                {currentHelp.details && (
                  <div className="help-card-details">
                    {currentHelp.details.map((detail, idx) => (
                      <div key={idx} className="help-detail-item">
                        <div className="help-detail-label">{detail.label}</div>
                        <div className="help-detail-text">{detail.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {currentHelp.tip && (
                  <div className="help-card-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>{currentHelp.tip}</span>
                  </div>
                )}
              </div>

              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>External users are auto-detected by email domain</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Azure AD invitation grants secure access</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Users can hold multiple roles simultaneously</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Blacklisted users lose all system access</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Toggle strip — outer (right) edge */}
          <div className="help-toggle-strip">
            <button
              type="button"
              className="help-toggle-icon-btn"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
            <button
              type="button"
              className="help-toggle-tab"
              onClick={() => setShowHelp(!showHelp)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};